/**
 * The pure routing decisions behind `useOptionsUiRedirect` (see that file for the rules), kept free
 * of React and the app store so they can be unit-tested on their own.
 */

export interface OptionsUiUserData {
    new_options_ui?: boolean;
    options_platform_subscriber?: boolean;
    options_legacy?: boolean;
}

/**
 * The single routing decision: should this user be moved OFF a legacy
 * options page?
 *
 * Returns `undefined` while undecidable (payload not loaded, or a
 * backend that predates the flags), `true` when the user should be
 * forwarded to the platform, `false` when the legacy page is where they
 * belong. Shared by the redirect hook and by legacy-page UI that must
 * not flash for users who are about to leave (e.g. the platform promo
 * modal — its impressions were being inflated by users who never
 * actually saw it).
 */
export function shouldLeaveLegacyOptions(userData: OptionsUiUserData | null | undefined): boolean | undefined {
    // `undefined` means ONLY "not decidable yet because the payload
    // hasn't arrived" — callers rely on that to hold the legacy page
    // back rather than flash it. Every decidable case returns a boolean.
    if (!userData) return undefined;
    // Entitled = the paid addon OR the backend flag — same pair the
    // OPTIONS nav tab uses (StockLinks), so link target and redirect
    // agree about which interface a user belongs on.
    const flag = typeof userData.new_options_ui === "boolean" || typeof userData.options_platform_subscriber === "boolean" ? !!(userData.new_options_ui || userData.options_platform_subscriber) : undefined;
    // Older backend that sends neither flag, or a logged-out visitor:
    // fail OPEN onto the legacy pages rather than holding a blank screen.
    if (typeof flag !== "boolean") return false;
    if (flag) return true;
    // No grandfathered access → the old pages aren't for them. Requires
    // the backend to SEND options_legacy (deploy-order guard: an older
    // backend omits it and nothing moves).
    if (typeof userData.options_legacy === "boolean" && !userData.options_legacy) return true;
    return false;
}

/**
 * Where the platform shows what a legacy `/options?tab=` view showed. The menu links to those
 * tabs, so without this a platform user picking Interesting Options Flow landed on Options News.
 */
export function legacyOptionsCounterpart(tab: string | string[] | undefined): string {
    if (tab === "interesting-options-flow") return "/options-platform/flow";
    if (tab === "option-flow-sentiment") return "/options-platform/sentiment";
    return "/options-platform/news";
}

/**
 * The page a legacy options page should prefetch while the user payload is still on its way, or
 * null. A direct load of a legacy page by a platform user can only be forwarded once that payload
 * lands; prefetching the platform's code meanwhile means the forward does not then wait for it.
 *
 * Only readers with a stored sign-in (`hasSession`): a signed-out visitor or a crawler can never be
 * entitled, and would otherwise download the options platform for nothing. Nothing either once the
 * payload has decided the reader stays on the legacy page.
 */
export function optionsPlatformPrefetchHref(pageIs: "legacy" | "new", counterpartHref: string | null, userData: OptionsUiUserData | null | undefined, hasSession: boolean): string | null {
    if (pageIs !== "legacy" || !counterpartHref || !hasSession) return null;
    if (shouldLeaveLegacyOptions(userData) === false) return null;
    return counterpartHref;
}
